import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineUserGroup } from "react-icons/hi2";
import { FaMapMarkerAlt, FaSearch, FaHotel, FaStar, FaWifi, FaSwimmingPool, FaCar, FaDumbbell, FaUtensils, FaSnowflake, FaCalendarAlt } from "react-icons/fa";
import { MdSpa, MdRoomService } from "react-icons/md";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";

/* ─── React Datepicker Imports ─── */
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import "./AllHotels.css";

/* ─── Amenity map ─── */
const AMENITY_MAP = {
  wifi: { label: "WiFi", icon: <FaWifi /> },
  free_wifi: { label: "Free WiFi", icon: <FaWifi /> },
  pool: { label: "Pool", icon: <FaSwimmingPool /> },
  swimming_pool: { label: "Pool", icon: <FaSwimmingPool /> },
  parking: { label: "Parking", icon: <FaCar /> },
  free_parking: { label: "Parking", icon: <FaCar /> },
  gym: { label: "Gym", icon: <FaDumbbell /> },
  fitness: { label: "Gym", icon: <FaDumbbell /> },
  breakfast: { label: "Breakfast", icon: <FaUtensils /> },
  restaurant: { label: "Restaurant", icon: <FaUtensils /> },
  spa: { label: "Spa", icon: <MdSpa /> },
  ac_unit: { label: "A/C", icon: <FaSnowflake /> },
  ac: { label: "A/C", icon: <FaSnowflake /> },
  room_service: { label: "Room Service", icon: <MdRoomService /> },
};

const HOTEL_TYPES = ["hotel", "resort", "villa", "dhramshala", "guest_house", "heritage"];
const TYPE_LABELS = {
  hotel: "Hotel", resort: "Resort", villa: "Villa",
  dhramshala: "Dharamshala", guest_house: "Guest House", heritage: "Heritage"
};
const TYPE_COLORS = {
  hotel: "#1e40af", resort: "#0f766e", villa: "#7c3aed",
  dhramshala: "#b45309", guest_house: "#be185d", heritage: "#92400e",
};
const GRADIENTS = [
  "linear-gradient(135deg,#0c1e35,#0c4a6e)",
  "linear-gradient(135deg,#1a1a2e,#0f3460)",
  "linear-gradient(135deg,#1f4037,#2d8653)",
  "linear-gradient(135deg,#373b44,#5b6472)",
  "linear-gradient(135deg,#4b1248,#3d5a80)",
];

const Stars = ({ n = 4 }) => (
  <span style={{ color: "#f59e0b", fontSize: 11, display: "flex", gap: 1 }}>
    {[1, 2, 3, 4, 5].map(i => <FaStar key={i} style={{ opacity: i <= n ? 1 : 0.25 }} />)}
  </span>
);

const AllHotels = () => {
  const navigate = useNavigate();

  const [showTravelerModal, setShowTravelerModal] = useState(false);
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hotels, setHotels] = useState([]);

  const [citySearch, setCitySearch] = useState({ query: "", results: [] });

  /* ─── State for Dates (Objects for DatePicker) ─── */
  const [checkInDate, setCheckInDate] = useState(new Date());
  const [checkOutDate, setCheckOutDate] = useState(new Date(new Date().setDate(new Date().getDate() + 1)));

  const [formData, setFormData] = useState({ 
    city: "", 
    hotel_type: "", 
    adults: 2, 
    children: 0, 
    rooms: 1 
  });

  const [filters, setFilters] = useState({ types: [], hasPrice: false, hasAmenities: false });
  const [sortBy, setSortBy] = useState("default");

  const fetchHotels = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        city: formData.city,
        hotel_type: formData.hotel_type,
      });
      const res = await axios.get(`${baseurl}/getGlobalHotels?${params.toString()}`);
      console.log("res", res);
      if (res.data.status === "success") setHotels(res.data.data);
    } catch (err) { console.error("Fetch Error", err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchHotels(); }, [formData.city, formData.hotel_type]);

  const handleCitySearch = async (query) => {
    setCitySearch(prev => ({ ...prev, query }));
    if (query.length > 2) {
      try {
        const res = await axios.get(`${baseurl}/searchHotelCityList?q=${query}`);
        setCitySearch(prev => ({ ...prev, results: res.data.data || [] }));
      } catch (err) { console.error(err); }
    } else {
      setCitySearch(prev => ({ ...prev, results: [] }));
    }
  };

  const updateCount = (field, op) =>
    setFormData(prev => ({
      ...prev,
      [field]: op === "inc" ? prev[field] + 1
        : Math.max(field === "adults" || field === "rooms" ? 1 : 0, prev[field] - 1)
    }));

  const handleViewDetails = (hotelObj) => {
    // Passing the ID/UUID through state instead of the URL path
    navigate(`/hotel-details/${hotelObj.uuid}`, { 
        state: { 
            hotel_id: hotelObj.hotel_id, 
            checkIn: checkInDate.toISOString().split('T')[0], 
            checkOut: checkOutDate.toISOString().split('T')[0],
            rooms: formData.rooms,
            adults: formData.adults,
            children: formData.children
        } 
    });
  };

  /* ─── Date Logic ─── */
  const handleCheckInChange = (date) => {
    setCheckInDate(date);
    if (date >= checkOutDate) {
      const nextDay = new Date(date);
      nextDay.setDate(nextDay.getDate() + 1);
      setCheckOutDate(nextDay);
    }
  };

  const displayed = hotels
    .filter(h => {
      if (filters.types.length && !filters.types.includes(h.hotel_type)) return false;
      if (filters.hasPrice && !h.weekly_prices) return false;
      if (filters.hasAmenities && (!h.amenities || h.amenities.length === 0)) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "price_asc") return (a.weekly_prices?.mon ?? Infinity) - (b.weekly_prices?.mon ?? Infinity);
      if (sortBy === "price_desc") return (b.weekly_prices?.mon ?? 0) - (a.weekly_prices?.mon ?? 0);
      return 0;
    });

  const toggleType = (t) =>
    setFilters(p => ({ ...p, types: p.types.includes(t) ? p.types.filter(x => x !== t) : [...p.types, t] }));

  const clearFilters = () => setFilters({ types: [], hasPrice: false, hasAmenities: false });

  const FilterPanel = () => (
    <div className="ah-filter-panel">
      <div className="ah-filter-head">
        <span className="ah-filter-title">Filters</span>
        <button className="ah-clear-btn" onClick={clearFilters}>Clear all</button>
      </div>

      <div className="ah-filter-section">
        <p className="ah-filter-label">Property Type</p>
        {HOTEL_TYPES.map(t => (
          <label key={t} className="ah-check-label">
            <span className={`ah-checkbox ${filters.types.includes(t) ? "on" : ""}`} onClick={() => toggleType(t)}>
              {filters.types.includes(t) && "✓"}
            </span>
            <span className="ah-check-text">{TYPE_LABELS[t]}</span>
            <span className="ah-check-count">{hotels.filter(h => h.hotel_type === t).length}</span>
          </label>
        ))}
      </div>

      <div className="ah-filter-section">
        <p className="ah-filter-label">Quick Filters</p>
        <label className="ah-check-label">
          <span className={`ah-checkbox ${filters.hasPrice ? "on" : ""}`}
            onClick={() => setFilters(p => ({ ...p, hasPrice: !p.hasPrice }))}>
            {filters.hasPrice && "✓"}
          </span>
          <span className="ah-check-text">With Pricing</span>
        </label>
        <label className="ah-check-label">
          <span className={`ah-checkbox ${filters.hasAmenities ? "on" : ""}`}
            onClick={() => setFilters(p => ({ ...p, hasAmenities: !p.hasAmenities }))}>
            {filters.hasAmenities && "✓"}
          </span>
          <span className="ah-check-text">Has Amenities</span>
        </label>
      </div>
    </div>
  );

  const HotelCard = ({ hotel, idx }) => {
    const price = hotel.weekly_prices?.mon;
    const hasImg = hotel.image && hotel.image.length > 0;
    const typeColor = TYPE_COLORS[hotel.hotel_type] || "#374151";
    const amenities = hotel.amenities || [];

    return (
      <div className="ah-card" style={{ animationDelay: `${idx * 60}ms` }}>
        <div className="ah-card-img" style={!hasImg ? { background: GRADIENTS[idx % GRADIENTS.length] } : {}}>
          {hasImg && (
            <img
              src={`${imgurl}/${hotel.image}`}
              alt={hotel.hotel_name}
              onError={e => {
                e.target.style.display = "none";
                e.target.parentNode.style.background = GRADIENTS[idx % GRADIENTS.length];
              }}
            />
          )}
          
          <span className="ah-type-badge" style={{ background: typeColor }}>
            {TYPE_LABELS[hotel.hotel_type] || hotel.hotel_type}
          </span>
          {hotel.image?.length > 1 && (
            <span className="ah-img-count">+{hotel.image.length - 1} photos</span>
          )}
        </div> 

        <div className="ah-card-body">
          <div className="ah-card-top">
            <div className="ah-card-info">
              <h2 className="ah-hotel-name">{hotel.hotel_name}</h2>
              <p className="ah-hotel-addr">
                <FaMapMarkerAlt style={{ color: "#f59e0b", marginRight: 4, flexShrink: 0, marginTop: 2 }} />
                {hotel.hotel_address}{hotel.city_village ? `, ${hotel.city_village}` : ""}
                {hotel.state_pincode ? ` – ${hotel.state_pincode}` : ""}
              </p>
              <div className="ah-rating-row">
                <span className="ah-rating-pill">4.0</span>
                <Stars n={4} />
                <span className="ah-rating-text">Good</span>
              </div>
              {hotel.room_name && (
                <div className="ah-room-chip">
                  <FaHotel style={{ fontSize: 10 }} /> {hotel.room_name}
                  {hotel.rooms_count ? ` · ${hotel.rooms_count} rooms` : ""}
                  {hotel.max_adults ? ` · Max ${hotel.max_adults} adults` : ""}
                </div>
              )}
            </div>

            <div className="ah-card-price">
              {price ? (
                <>
                  <p className="ah-price-label">Per night</p>
                  <div className="ah-price">₹{Number(price).toLocaleString("en-IN")}</div>
                  <p className="ah-price-tax">+ taxes & fees</p>
                  <button className="ah-book-btn" onClick={() => handleViewDetails(hotel)}>
                    View Options
                  </button>
                </>
              ) : (
                <>
                  <p className="ah-no-price">Price on request</p>
                  <button className="ah-book-btn ah-book-outline" onClick={() => handleViewDetails(hotel)}>
                    View Details
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="ah-amenities">
            {amenities.length > 0 ? (
              <>
                {amenities.slice(0, 5).map(ami => {
                  const a = AMENITY_MAP[ami] || { label: ami.replace(/_/g, " "), icon: null };
                  return (
                    <span key={ami} className="ah-ami-chip">
                      {a.icon && <span className="ah-ami-icon">{a.icon}</span>}
                      {a.label}
                    </span>
                  );
                })}
                {amenities.length > 5 && <span className="ah-ami-more">+{amenities.length - 5} more</span>}
              </>
            ) : (
              <span className="ah-ami-na">Contact hotel for amenities</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="ah-root">
        <section className="ah-hero">
          <div className="ah-hero-inner">
            <p className="ah-hero-label">Discover India</p>
            <h1 className="ah-hero-title">Find the Best Stays</h1>
            <p className="ah-hero-sub">Book hotels, resorts and dharamshalas at best prices</p>
          </div>
        </section>

        <div className="ah-search-wrap">
          <div className="ah-search-card">
            <div className="ah-field" style={{ position: "relative" }}>
              <label className="ah-field-lbl"><FaMapMarkerAlt /> Destination</label>
              <input
                className="ah-finput"
                placeholder="Search city…"
                value={citySearch.query}
                onChange={e => handleCitySearch(e.target.value)}
              />
              {citySearch.results.length > 0 && (
                <div className="ah-autocomplete">
                  {citySearch.results.map(item => (
                    <button key={item.city_code} onClick={() => {
                      setFormData(p => ({ ...p, city: item.city_name }));
                      setCitySearch({ query: item.city_name, results: [] });
                    }}>
                      <FaMapMarkerAlt style={{ color: "#f59e0b" }} /> {item.city_name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="ah-field">
              <label className="ah-field-lbl"><FaCalendarAlt /> Check-in</label>
              <DatePicker
                selected={checkInDate}
                onChange={handleCheckInChange}
                minDate={new Date()}
                className="ah-finput"
                dateFormat="dd MMM yyyy"
              />
            </div>

            <div className="ah-field">
              <label className="ah-field-lbl"><FaCalendarAlt /> Check-out</label>
              <DatePicker
                selected={checkOutDate}
                onChange={(date) => setCheckOutDate(date)}
                minDate={new Date(checkInDate.getTime() + 86400000)}
                className="ah-finput"
                dateFormat="dd MMM yyyy"
              />
            </div>

            <div className="ah-field">
              <label className="ah-field-lbl"><HiOutlineUserGroup /> Guests & Rooms</label>
              <div className="ah-guest-trigger" onClick={() => setShowTravelerModal(true)}>
                <span>{formData.rooms} Room · {formData.adults + formData.children} Guests</span>
                <span style={{ color: "var(--muted)", fontSize: 12 }}>▾</span>
              </div>
            </div>

            <button className="ah-search-btn" onClick={fetchHotels}>
              <FaSearch /> Search
            </button>
          </div>
        </div>

        <div className="ah-layout">
          <aside className="ah-sidebar"><FilterPanel /></aside>

          <main className="ah-main">
            <div className="ah-results-bar">
              <div className="ah-results-title">
                {displayed.length} Properties
                <span>in {formData.city || "India"}</span>
              </div>
              <div className="ah-bar-right">
                <button className="ah-mob-filter-btn" onClick={() => setShowMobileFilter(true)}>
                  ⚙ Filters
                </button>
                <select className="ah-sort" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                  <option value="default">Sort: Default</option>
                  <option value="price_asc">Price: Low → High</option>
                  <option value="price_desc">Price: High → Low</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="ah-loading">
                <div className="ah-spinner" />
                <p>Finding the best stays for you…</p>
              </div>
            ) : displayed.length === 0 ? (
              <div className="ah-empty">
                <div className="ah-empty-icon">🏨</div>
                <h3>No properties found</h3>
                <p>Try adjusting your search or filters.</p>
              </div>
            ) : (
              displayed.map((hotel, idx) => (
                <HotelCard key={hotel.uuid || hotel.hotel_id} hotel={hotel} idx={idx} />
              ))
            )}
          </main>
        </div>
      </div>

      {showTravelerModal && (
        <div className="ah-backdrop" onClick={e => e.target === e.currentTarget && setShowTravelerModal(false)}>
          <div className="ah-modal">
            <div className="ah-modal-head">
              <h2>Guests & Rooms</h2>
              <button className="ah-modal-close" onClick={() => setShowTravelerModal(false)}>×</button>
            </div>
            {[
              { label: "Rooms", key: "rooms", sub: "No. of rooms" },
              { label: "Adults", key: "adults", sub: "Ages 13+" },
              { label: "Children", key: "children", sub: "Ages 2–12" },
            ].map(row => (
              <div className="ah-counter-row" key={row.key}>
                <div>
                  <div className="ah-counter-label">{row.label}</div>
                  <div className="ah-counter-sub">{row.sub}</div>
                </div>
                <div className="ah-counter-ctrl">
                  <button className="ah-ctr-btn" onClick={() => updateCount(row.key, "dec")}>−</button>
                  <span className="ah-ctr-val">{formData[row.key]}</span>
                  <button className="ah-ctr-btn" onClick={() => updateCount(row.key, "inc")}>+</button>
                </div>
              </div>
            ))}
            <button className="ah-modal-done" onClick={() => setShowTravelerModal(false)}>Done</button>
          </div>
        </div>
      )}

      {showMobileFilter && (
        <div className="ah-drawer-wrap">
          <div className="ah-drawer">
            <div className="ah-drawer-head">
              <span className="ah-drawer-title">Filters</span>
              <button className="ah-modal-close" onClick={() => setShowMobileFilter(false)}>×</button>
            </div>
            <FilterPanel />
            <button className="ah-modal-done" onClick={() => setShowMobileFilter(false)} style={{ marginTop: 16 }}>
              Show {displayed.length} Results
            </button>
          </div>
          <div className="ah-drawer-overlay" onClick={() => setShowMobileFilter(false)} />
        </div>
      )}
    </>
  );
};

export default AllHotels;